import moment from 'moment';
import { FormControl, FormHelperText, Grid, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { DatePicker, PickersDay } from '@mui/x-date-pickers';
import { isEmpty } from 'lodash';
import { styled } from '@mui/material/styles';
import { useFormikContext } from 'formik';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { EditOrderFormValues } from '../../EditOrderDialog';
import {
  AccountData,
  DateAvailabilityOut,
  OrderPageTypes,
  TimeAvailabilityDisabledReason,
  TimeSlotResponseOut,
} from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../../../common/getDateAndTimeFormat';
import { useEditOrder } from '../../EditOrderContext';
import { DATE_FORMAT } from '../../../../calendarWrapper/CalendarWrapper';
import {
  activeTickets,
  findMultipleTicketsUiObject,
  getPerTicketCapacityTicketId,
} from '../../../../../common/multipleTicketsUtils';

export const OccupiedPickersDay = styled(PickersDay)(() => ({
  textDecoration: 'line-through',
}));

export const AvailablePickersDay = styled(PickersDay)(({ theme }) => ({
  border: `1px dashed ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  '&.Mui-selected': {
    border: 'none',
    color: theme.palette.primary.contrastText,
  },
}));

type DateAndEventTimeFieldsProps = {
  availableDatesForEvents: DateAvailabilityOut[];
};

// Renders the date picker and, for events and courses, the session time select. Returns a fragment so the
// parent decides the layout container.
export const DateAndEventTimeFields = ({ availableDatesForEvents }: DateAndEventTimeFieldsProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const { longDateFormat } = getDateFormat(account);
  const { t } = useTranslation();
  const [dateOpen, setDateOpen] = useState(false);
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState, setDate: setDateInState } = useEditOrder();

  const isEventOrCourse = [OrderPageTypes.EVENT, OrderPageTypes.COURSE].includes(
    editOrderState.orderPage.order_page_type,
  );
  const shouldDisableDate = (value: moment.Moment) => {
    if (!isEventOrCourse || isEmpty(availableDatesForEvents)) return false;
    return !availableDatesForEvents.find((x) => x.date === value.format(DATE_FORMAT));
  };
  const handleDateChange = (newValue: moment.Moment | null) => {
    if (!newValue) return;
    void setFieldValue('date', newValue.format(DATE_FORMAT));
    setDateInState(newValue);
    if (isEventOrCourse) {
      const selectedTime = availableDatesForEvents.find((x) => x.date === newValue.format(DATE_FORMAT))?.times[0]
        ?.seconds;
      if (selectedTime !== undefined) void setFieldValue('timeSeconds', selectedTime);
    }
  };

  const language = useLanguage();
  // An empty date (a copied order awaiting a new date) shows an empty picker, not an invalid moment.
  const dateValue = values.date ? moment(values.date) : null;
  const { timeSeconds } = values;
  const availableTimesForEvent =
    availableDatesForEvents.find((x) => x.date === values.date)?.times ?? availableDatesForEvents[0]?.times ?? [];

  const multipleTicketsUiObject = findMultipleTicketsUiObject(editOrderState.orderPage);
  const resolveSlotSoldOutLabel = (slot: TimeSlotResponseOut): string | undefined => {
    const ticketId = getPerTicketCapacityTicketId(slot);
    if (!ticketId || !multipleTicketsUiObject) return undefined;
    const ticket = activeTickets(multipleTicketsUiObject).find((t) => t.id === ticketId);
    return ticket?.label?.[language as keyof typeof ticket.label];
  };
  const selectedSlot = availableTimesForEvent.find((x) => x.seconds === timeSeconds);
  const selectedSlotSoldOutLabel = selectedSlot ? resolveSlotSoldOutLabel(selectedSlot) : undefined;

  const renderWeekPickerDay = (props: any) => {
    if (props.disabled) {
      return <OccupiedPickersDay {...props} />;
    }
    if (isEventOrCourse && availableDatesForEvents.find((x) => x.date === props.day.format(DATE_FORMAT))) {
      return <AvailablePickersDay {...props} />;
    }
    return <PickersDay {...props} />;
  };

  return (
    <>
      <DatePicker
        open={dateOpen}
        onOpen={() => setDateOpen(true)}
        onClose={() => setDateOpen(false)}
        label={t('calendar.edit_order.date')}
        slots={{
          // @ts-ignore
          day: renderWeekPickerDay,
        }}
        format={longDateFormat}
        value={dateValue}
        shouldDisableDate={shouldDisableDate}
        onChange={handleDateChange}
        sx={{ maxWidth: 180 }}
      />
      {isEventOrCourse && (
        <Grid item sx={{ flexShrink: 0 }}>
          {!isEmpty(availableTimesForEvent) ? (
            <FormControl>
              <Select
                value={timeSeconds}
                renderValue={() => moment.unix(timeSeconds).tz('utc').format(timeFormat)}
                onChange={(event) => {
                  void setFieldValue('timeSeconds', +event.target.value);
                }}>
                {availableTimesForEvent.map((x) => {
                  const soldOutLabel = resolveSlotSoldOutLabel(x);
                  return (
                    <MenuItem value={x.seconds} key={`menuitem-${x.seconds}`} disabled={x.isDisabled}>
                      <Grid container alignItems="baseline" gap={1}>
                        <span>{moment.unix(x.seconds).tz('utc').format(timeFormat)}</span>
                        {soldOutLabel && (
                          <Typography variant="caption" color="warning.main">
                            {t('calendar.edit_order.time_disable_reason.per_ticket_capacity_exceeded', {
                              ticket: soldOutLabel,
                            })}
                          </Typography>
                        )}
                      </Grid>
                    </MenuItem>
                  );
                })}
              </Select>
              {selectedSlot?.timeDisableReason === TimeAvailabilityDisabledReason.PER_TICKET_CAPACITY_EXCEEDED &&
                selectedSlotSoldOutLabel && (
                  <FormHelperText error>
                    {t('calendar.edit_order.time_disable_reason.per_ticket_capacity_exceeded', {
                      ticket: selectedSlotSoldOutLabel,
                    })}
                  </FormHelperText>
                )}
            </FormControl>
          ) : (
            <Grid />
          )}
        </Grid>
      )}
    </>
  );
};
